import { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';

import { SearchQueryProps } from '@/components/app/cards/ContentCard/types';
import ContentChip from '@/components/app/cards/ContentChip';
import {
	isNarrator,
	isProgramOrGuide,
	isRenderablePackItem,
	Pack,
	PackCardItem,
	RenderablePackItem,
} from '@/components/app/cards/PacksList/types';
import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import NarratorRow from '@/components/app/ui/NarratorRow';
import PackClassLink from '@/components/app/ui/PackClassLink';
import Image from '@/components/image/NextImage';
import { useIsUnlockedForUser } from '@/hooks/app/useIsUnlockedForUser';
import { usePackItemAttributes } from '@/hooks/app/usePackItemAttributes';
import { useGuideState } from '@/hooks/store';
import { formatResizedUrl } from '@/utils/app/formatResizedUrl';

import messages from './messages';
import {
	CoverArtWrapper,
	HeaderText,
	InfoContainer,
	PageTitle,
	PauseIcon,
	PlayButton,
	PlayIcon,
	PlaylistControls,
	PlaylistDescription,
	PlaylistHeaderWrapper,
	PlaylistInfo,
	PlaylistTitle,
	RowBadge,
	RowContainer,
	RowLine,
	RowTitle,
	RowWrapper,
	ShuffleButton,
	ShuffleIcon,
	Subtitle,
	ThumbnailWrapper,
	Title,
	TitleContainer,
	TrackSummaryText,
	Wrapper,
} from './styles';
import { PackRowListProps } from './types';
import { getPlaylistDuration, getRowLines, isEpisodicPack, isPlayableTrack } from './utils';

// The thumbnail is a fixed small square, unlike ContentCard's tile which scales with viewport —
// requesting one fixed size avoids re-requesting a resized asset per breakpoint.
const ROW_THUMBNAIL_SIZE = 128;

interface PackRowProps {
	pack: Pack;
	packItem: RenderablePackItem;
	packFeedName?: string;
	searchQuery?: SearchQueryProps;
}

const PackRow = ({ pack, packItem, packFeedName, searchQuery }: PackRowProps) => {
	const { formatMessage } = useIntl();
	const {
		title,
		subtitle,
		duration,
		durationSeconds,
		assetType,
		image,
		content_type,
		narrator_name,
		promotional_text,
		description,
		is_new,
		isLocked,
		handlePackItemClick,
	} = usePackItemAttributes({
		pack,
		packItem: packItem as PackCardItem,
		packFeedName,
		searchQuery,
	});

	// The aria-label carries only the title, which repeats across rows (e.g. every Daily Move episode),
	// so the detail lines are linked as the description to keep each row distinguishable.
	const lineIdPrefix = useId();
	const isPlaylistPack = pack.pack_type === 'playlist';
	const { secondary, tertiary } = getRowLines({
		itemType: packItem.type,
		contentType: content_type,
		narratorName: narrator_name,
		subtitle,
		duration,
		description,
		promotionalText: promotional_text,
	});

	const secondaryId = `${lineIdPrefix}-secondary`;
	const tertiaryId = `${lineIdPrefix}-tertiary`;
	const badgeId = `${lineIdPrefix}-new`;
	const tag = 'tag' in packItem ? packItem.tag : undefined;
	const badgeText = isPlaylistPack ? undefined : tag || (is_new ? formatMessage(messages.newTag) : undefined);
	const describedBy =
		[secondary && secondaryId, tertiary && tertiaryId, badgeText && badgeId].filter(Boolean).join(' ') ||
		undefined;

	return (
		<RowWrapper>
			<RowContainer
				onClick={() => {
					handlePackItemClick().catch(() => {});
				}} // Don't pass e to the above click event
				onKeyDown={e => {
					if (e.nativeEvent instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') {
						return;
					}
					// Space's default action is page scroll; this row has no href for it to fall
					// back to, so without this the row activates AND the page scrolls under it.
					e.preventDefault();
					handlePackItemClick().catch(() => {});
				}}
				tabIndex={0}
				role="button"
				aria-label={formatMessage(isLocked ? messages.lockedLabel : messages.label, { title })}
				aria-describedby={describedBy}
			>
				<ThumbnailWrapper $isCircle={isNarrator(packItem)}>
					{/* An item the client has no render branch for reaches here with no usable
					    asset. formatResizedUrl calls .replace on its first argument, and next/image
					    rejects an empty src, so both would throw during render — render the row
					    without artwork instead of taking the surface down. */}
					{image?.url && (
						<Image
							fill
							style={{ objectFit: 'cover' }}
							src={formatResizedUrl(image.url, ROW_THUMBNAIL_SIZE, ROW_THUMBNAIL_SIZE)}
							alt=""
							sizes="80px"
						/>
					)}
					{/* The circular narrator crop clips a corner-hugging chip, so it gets a smaller chip pulled inward. */}
					<ContentChip
						isLocked={Boolean(isLocked)}
						durationSeconds={isNarrator(packItem) ? undefined : durationSeconds}
						assetType={assetType}
						margin={isNarrator(packItem) ? 16 : 4}
						size={isNarrator(packItem) ? 'compact' : 'default'}
					/>
				</ThumbnailWrapper>
				<InfoContainer>
					{title && <RowTitle>{title}</RowTitle>}
					{secondary && <RowLine id={secondaryId}>{secondary}</RowLine>}
					{tertiary && <RowLine id={tertiaryId}>{tertiary}</RowLine>}
				</InfoContainer>
			</RowContainer>
			{badgeText && (
				<RowBadge id={badgeId} $isTag={Boolean(tag)} data-testid="new-icon">
					{badgeText}
				</RowBadge>
			)}
		</RowWrapper>
	);
};

interface PackPlaybackProxyProps {
	pack: Pack;
	packItem: RenderablePackItem;
	packFeedName?: string;
	onTriggered: () => void;
}

// Renders nothing — exists only to call usePackItemAttributes for a single chosen item and
// fire its handlePackItemClick once mounted. This reuses the exact same locked-content/upsell
// routing and unlocked-guide-play routing every row already goes through on a real click,
// instead of a second hand-rolled path for the header's Play/Shuffle buttons.
const PackPlaybackProxy = ({ pack, packItem, packFeedName, onTriggered }: PackPlaybackProxyProps) => {
	const { handlePackItemClick } = usePackItemAttributes({
		pack,
		packItem: packItem as PackCardItem,
		packFeedName,
	});

	useEffect(() => {
		// The parent only mounts this proxy at the moment a target item is chosen, and
		// unmounts it via onTriggered once the click resolves — so this is deliberately a
		// fire-once-per-mount effect, not one that should re-run if handlePackItemClick's
		// identity changes for unrelated reasons.
		handlePackItemClick()
			.catch(() => {})
			.finally(onTriggered);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

interface PlaylistHeaderProps {
	pack: Pack;
	supportedItems: RenderablePackItem[];
	packFeedName?: string;
}

const PLAYLIST_COVER_ART_SIZE = 320;

const PlaylistHeader = ({ pack, supportedItems, packFeedName }: PlaylistHeaderProps) => {
	const { formatMessage } = useIntl();
	// Holds the single item a Play/Shuffle click just chose, so exactly one PackPlaybackProxy
	// mounts, fires its click once, then unmounts (see onTriggered) — ready for the next click,
	// even a repeat pick of the same item.
	const [clickTarget, setClickTarget] = useState<RenderablePackItem | null>(null);

	const currentGuide = useGuideState();
	const { playerMode, onPause, onPlay } = useMediaPlayerContext();
	// Only a track that belongs to this playlist drives the button, so another page's audio doesn't.
	const isPlaylistLoaded =
		playerMode !== 'stopped' &&
		Boolean(currentGuide) &&
		supportedItems.some(item => isPlayableTrack(item) && item.guide_id === currentGuide?.id);
	const isPlaylistPlaying = isPlaylistLoaded && playerMode === 'playing';

	const isUnlockedForUser = useIsUnlockedForUser();
	const playableItems = supportedItems.filter(item => isUnlockedForUser(item, pack) && isPlayableTrack(item));
	// supportedItems is guaranteed non-empty here — PackRowList returns null before rendering
	// this header when there are zero renderable items.
	const fallbackItem = supportedItems.find(isPlayableTrack);
	const curator = pack.associations?.[0];
	const trackCount = pack.total_item_number ?? supportedItems.length;
	const playlistDuration = getPlaylistDuration(pack.items, pack.total_item_number);
	const title = pack.title ?? '';

	return (
		<PlaylistHeaderWrapper>
			<CoverArtWrapper>
				{pack.image?.url && (
					<Image
						fill
						style={{ objectFit: 'cover' }}
						src={formatResizedUrl(pack.image.url, PLAYLIST_COVER_ART_SIZE, PLAYLIST_COVER_ART_SIZE)}
						alt=""
						sizes="160px"
					/>
				)}
			</CoverArtWrapper>
			<PlaylistInfo>
				{pack.title && <PlaylistTitle>{pack.title}</PlaylistTitle>}
				{curator && (
					<NarratorRow
						image={curator.image}
						title={curator.title}
						href={curator.narrator_id ? `/app/narrator-${curator.narrator_id}` : undefined}
						ariaLabel={formatMessage(messages.curatorAriaLabel, { curator: curator.title })}
					/>
				)}
				<PlaylistControls>
					<PlayButton
						type="button"
						disabled={!fallbackItem}
						aria-label={formatMessage(isPlaylistPlaying ? messages.pauseAriaLabel : messages.playAriaLabel, {
							title,
						})}
						onClick={() => {
							if (isPlaylistPlaying) {
								onPause();
								return;
							}
							// A paused track from this playlist resumes in place rather than restarting at track one.
							if (isPlaylistLoaded) {
								onPlay();
								return;
							}
							setClickTarget(playableItems[0] ?? fallbackItem ?? null);
						}}
					>
						{isPlaylistPlaying ? <PauseIcon /> : <PlayIcon />}
						{formatMessage(isPlaylistPlaying ? messages.pauseLabel : messages.playLabel)}
					</PlayButton>
					<ShuffleButton
						type="button"
						disabled={!fallbackItem}
						aria-label={formatMessage(messages.shuffleAriaLabel, { title })}
						onClick={() => {
							const randomUnlocked =
								playableItems.length > 0
									? playableItems[Math.floor(Math.random() * playableItems.length)]
									: undefined;
							setClickTarget(randomUnlocked ?? fallbackItem ?? null);
						}}
					>
						<ShuffleIcon>
							<polyline points="16 3 21 3 21 8" />
							<line x1="4" y1="20" x2="21" y2="3" />
							<polyline points="21 16 21 21 16 21" />
							<line x1="15" y1="15" x2="21" y2="21" />
							<line x1="4" y1="4" x2="9" y2="9" />
						</ShuffleIcon>
						{formatMessage(messages.shuffleLabel)}
					</ShuffleButton>
				</PlaylistControls>
				<TrackSummaryText>
					{playlistDuration
						? formatMessage(messages.trackSummary, { count: trackCount, ...playlistDuration })
						: formatMessage(messages.trackCount, { count: trackCount })}
				</TrackSummaryText>
				{pack.subtitle && <PlaylistDescription>{pack.subtitle}</PlaylistDescription>}
			</PlaylistInfo>
			{clickTarget && (
				<PackPlaybackProxy
					pack={pack}
					packItem={clickTarget}
					packFeedName={packFeedName}
					onTriggered={() => setClickTarget(null)}
				/>
			)}
		</PlaylistHeaderWrapper>
	);
};

const PackRowList = ({ pack, packFeedName, searchQuery, isPageHeading = false }: PackRowListProps) => {
	// Keep each item's position in the server's original (pre-filter) array so
	// pack_item_index reflects the server's ranking, matching how mobile stamps
	// position before any client-side filtering — not this component's own
	// render-order index into the type-filtered subset.
	const supportedItems = pack.items
		.map((packItem, serverIndex) => ({ packItem, serverIndex }))
		.filter((entry): entry is { packItem: RenderablePackItem; serverIndex: number } =>
			isRenderablePackItem(entry.packItem),
		);

	// A title/subtitle header over zero rows reads as a broken page, not an empty one —
	// hide the whole section rather than orphan the header above nothing.
	if (supportedItems.length === 0) {
		return null;
	}

	return (
		<>
			{pack.pack_type === 'playlist' ? (
				<PlaylistHeader
					pack={pack}
					supportedItems={supportedItems.map(({ packItem }) => packItem)}
					packFeedName={packFeedName}
				/>
			) : (
				<TitleContainer $isPageHeading={isPageHeading}>
					<HeaderText>
						{pack.title &&
							(isPageHeading ? <PageTitle>{pack.title}</PageTitle> : <Title>{pack.title}</Title>)}
						{pack.subtitle && !isEpisodicPack(pack) && <Subtitle>{pack.subtitle}</Subtitle>}
					</HeaderText>
					{pack.has_more && <PackClassLink pack={pack} />}
				</TitleContainer>
			)}
			<Wrapper>
				{supportedItems.map(({ packItem, serverIndex }) => (
					<PackRow
						pack={pack}
						// `pack`-type items carry neither guide_id nor program_id, so they always
						// fall through to the positional fallback below.
						key={
							(isProgramOrGuide(packItem) ? (packItem.guide_id ?? packItem.program_id) : undefined) ??
							`${pack.id}-${serverIndex}`
						}
						packItem={packItem}
						packFeedName={packFeedName}
						searchQuery={{
							...searchQuery,
							pack_item_index: serverIndex,
						}}
					/>
				))}
			</Wrapper>
		</>
	);
};

export default PackRowList;
